import { useState, useEffect, useMemo } from "react";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { AlertTriangle, Sparkles, Download, Loader2, FileText, ChevronRight } from "lucide-react";
import { apiCall } from "../App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const PILLAR_COLORS = {
  P1: "#fbbf24", // amber
  P2: "#f472b6", // pink
  P3: "#a78bfa", // violet
  P4: "#fb7185", // rose
  P5: "#34d399", // emerald
};

// ─────────── RADAR CHART ───────────
export const MatrixRadarChart = ({ spec, clientResponses, coachResponses, height = 300 }) => {
  const data = useMemo(() => {
    if (!spec) return [];
    const computeAvg = (resp) => {
      if (!resp) return 0;
      const scores = [];
      for (const code in resp) {
        const r = resp[code] || {};
        if (typeof r.q1 === "number" && typeof r.q2 === "number") {
          scores.push(Math.round((r.q1 + r.q2) / 2));
        }
      }
      return scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
    };
    return spec.pillars.map(p => {
      const inPillar = p.elements.map(e => e.code);
      const filter = (resp) => Object.fromEntries(Object.entries(resp || {}).filter(([k]) => inPillar.includes(k)));
      return {
        pillar: `${p.code}\n${p.name}`,
        client: parseFloat(computeAvg(filter(clientResponses)).toFixed(2)),
        coach: parseFloat(computeAvg(filter(coachResponses)).toFixed(2)),
        fullMark: 4,
      };
    });
  }, [spec, clientResponses, coachResponses]);

  return (
    <div className="rounded-xl border border-divine-gold/15 bg-cosmic-deep/30 p-4" data-testid="matrix-radar-chart">
      <h3 className="font-heading text-divine-gold text-sm mb-3 flex items-center gap-2">
        <Sparkles className="w-4 h-4" /> Cosmic Balance · Per-Pillar Average
      </h3>
      <ResponsiveContainer width="100%" height={height}>
        <RadarChart data={data} outerRadius="75%">
          <PolarGrid stroke="#D4AF37" strokeOpacity={0.2} />
          <PolarAngleAxis dataKey="pillar" tick={{ fill: "#E0D5FF", fontSize: 11 }} />
          <PolarRadiusAxis angle={90} domain={[0, 4]} tick={{ fill: "#9b87b5", fontSize: 9 }} tickCount={5} />
          <Radar name="Client" dataKey="client" stroke="#D4AF37" fill="#D4AF37" fillOpacity={0.25} strokeWidth={2} />
          <Radar name="Coach" dataKey="coach" stroke="#34d399" fill="#34d399" fillOpacity={0.15} strokeWidth={1.5} strokeDasharray="4 3" />
          <Tooltip
            contentStyle={{ backgroundColor: "#1a1340", border: "1px solid #D4AF37", borderRadius: 6, fontSize: 11 }}
            labelStyle={{ color: "#D4AF37" }}
          />
          <Legend wrapperStyle={{ fontSize: 11, color: "#E0D5FF" }} />
        </RadarChart>
      </ResponsiveContainer>
      <p className="text-[10px] text-chakra-crown/40 text-center mt-1">Scale: 1 Tamas → 4 Sattva Expressed</p>
    </div>
  );
};


// ─────────── SUGGESTIONS PANEL ───────────
export const SuggestionsPanel = ({ token, isCoachView = false, userId = null, onCreatePlan }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const url = isCoachView
        ? `/coach-assessment-matrix/user/${userId}/suggestions`
        : "/coach-assessment-matrix/me/suggestions";
      const r = await apiCall("GET", url, null, token);
      if (cancelled) return;
      if (r.success) setData(r.data);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [token, isCoachView, userId]);

  if (loading) return <div className="p-4 text-chakra-crown/50 text-xs flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Computing suggestions…</div>;
  if (!data) return null;

  const { critical = [], growth = [], practice_refinement = [], total_flagged } = data;

  if (total_flagged === 0 && practice_refinement.length === 0) {
    return (
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 text-emerald-300 text-sm" data-testid="suggestions-empty">
        ✓ No critical or growth elements detected yet. Complete the Awareness diagnostic to surface intervention suggestions.
      </div>
    );
  }

  const Section = ({ title, color, icon: Icon, items, tone, testid }) => {
    if (!items.length) return null;
    return (
      <div className="rounded-xl border p-4" style={{ borderColor: color + "55", background: color + "0d" }} data-testid={testid}>
        <h4 className="font-heading text-sm mb-3 flex items-center gap-2" style={{ color }}>
          <Icon className="w-4 h-4" /> {title} <span className="text-[10px] opacity-70">· {items.length}</span>
        </h4>
        <div className="space-y-2">
          {items.map(it => (
            <div key={it.code} className="rounded-lg border border-divine-gold/10 bg-cosmic-deep/40 p-3" data-testid={`suggestion-${it.code}`}>
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div>
                  <p className="text-divine-gold text-xs font-heading">
                    {it.name} · <span className="italic">{it.sanskrit}</span>
                    <span className="ml-2 text-chakra-crown/40 text-[10px]">[{it.pillar_code} · {it.pillar_name}]</span>
                  </p>
                </div>
                <span className="shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold border" style={{ borderColor: color, color }}>
                  Score {it.score}/4
                </span>
              </div>
              {it.suggested_practice && (
                <p className="text-purple-300/85 text-[11px] mt-1">
                  <span className="text-chakra-crown/45 text-[10px] uppercase tracking-wider mr-1">Practice:</span>
                  {it.suggested_practice}
                </p>
              )}
              {it.first_step && (
                <p className="text-chakra-crown/65 text-[11px] mt-1 italic">→ {it.first_step}</p>
              )}
              {onCreatePlan && tone !== "info" && (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`create-plan-${it.code}`}
                  onClick={() => onCreatePlan(it)}
                  className="mt-2 border-purple-500/40 text-purple-300 hover:bg-purple-500/10 text-[11px] h-7"
                >
                  Create Intervention Plan <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3" data-testid="matrix-suggestions-panel">
      <Section title="Critical Elements" color="#fb7185" icon={AlertTriangle} items={critical} tone="critical" testid="suggestions-critical" />
      <Section title="Growth Elements" color="#fbbf24" icon={Sparkles} items={growth} tone="growth" testid="suggestions-growth" />
      <Section title="Practice Refinement" color="#60a5fa" icon={Sparkles} items={practice_refinement} tone="info" testid="suggestions-practice" />
    </div>
  );
};


// ─────────── PDF DOWNLOAD BUTTON ───────────
export const MatrixPdfButton = ({ token, isCoachView = false, userId = null, label = "Download PDF" }) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const url = isCoachView
        ? `${process.env.REACT_APP_BACKEND_URL}/api/coach-assessment-matrix/user/${userId}/pdf`
        : `${process.env.REACT_APP_BACKEND_URL}/api/coach-assessment-matrix/me/pdf`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const err = await res.text();
        toast.error(`PDF export failed: ${res.status} — ${err.slice(0, 100)}`);
        return;
      }
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const cd = res.headers.get("Content-Disposition") || "";
      const m = /filename="([^"]+)"/.exec(cd);
      link.download = m ? m[1] : "coach_assessment_matrix.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF download error");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Button
      onClick={handleDownload}
      disabled={downloading}
      data-testid="matrix-pdf-download-btn"
      size="sm"
      className="bg-divine-gold text-cosmic-void hover:bg-divine-gold/90 font-semibold"
    >
      {downloading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
      {label}
    </Button>
  );
};


// ─────────── DEFAULT EXPORT: Summary widget combining all three ───────────
const MatrixSummaryWidget = ({ spec, doc, token, isCoachView = false, userId = null, onCreatePlan }) => {
  if (!spec || !doc) return null;
  return (
    <div className="space-y-4" data-testid="matrix-summary-widget">
      <MatrixRadarChart
        spec={spec}
        clientResponses={doc.client_responses}
        coachResponses={doc.coach_responses}
      />
      <div className="rounded-xl border border-divine-gold/15 bg-cosmic-deep/30 p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-divine-gold" />
          <p className="text-chakra-crown/75 text-sm">Full multi-stage Vedic report PDF</p>
        </div>
        <MatrixPdfButton token={token} isCoachView={isCoachView} userId={userId} />
      </div>
      <SuggestionsPanel token={token} isCoachView={isCoachView} userId={userId} onCreatePlan={onCreatePlan} />
    </div>
  );
};

export default MatrixSummaryWidget;
